















































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { AxiosError } from 'axios';
import { useCreateHSCode, useUpdateHSCode } from '@/composable/hs-codes';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import { IHSCode, IHSCodeData, HSCode } from '@/models';
import { ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';
import { requiredValidator } from '@/utils/validators';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';

export default defineComponent({
  name: 'HSCodeFormDialog',
  components: {
    ALDialog,
    ErrorAlert
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },
    hsCode: {
      type: Object as PropType<IHSCodeData | null>,
      default: null
    }
  },
  emits: ['update:dialog'],
  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const hsCodeData = ref<IHSCodeData>({});

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        hsCodeData.value = !props.isEdit
          ? ({} as IHSCodeData)
          : cloneDeep(props.hsCode!);
      }
    });

    const formRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () => `${props.isEdit ? 'Update' : 'Create'} hsCode information`
    );

    function closeDialog() {
      clearErrors();
      localDialog.value = false;
    }

    const {
      isLoading: isUpdatePending,
      error: updateError,
      mutate: updateHSCode,
      reset: updateReset
    } = useUpdateHSCode();
    const {
      isLoading: isCreatePending,
      error: createError,
      mutate: createHSCode,
      reset: createReset
    } = useCreateHSCode();

    const localError = ref<ISystemError | null>(null);

    const loading = computed(() =>
      props.isEdit ? isUpdatePending.value : isCreatePending.value
    );

    const error = computed<ISystemError | null>({
      get() {
        let systemError: ISystemError | null = localError.value;
        const axiosError = props.isEdit ? updateError.value : createError.value;

        if (axiosError) {
          systemError = setErrorFromAxiosError(axiosError as AxiosError);
        }

        return systemError;
      },
      set(value: ISystemError | null) {
        localError.value = value;
      }
    });

    function clearErrors() {
      props.isEdit ? updateReset() : createReset();
      error.value = null;
    }

    function resetFormValidation(): void {
      if (formRef.value) {
        return formRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (formRef.value) {
        valid = formRef.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        let hsCodeInstance: IHSCode | null = null;

        try {
          hsCodeInstance = new HSCode(hsCodeData.value);
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (hsCodeInstance != null) {
          const saveAction = props.isEdit ? updateHSCode : createHSCode;

          let payload = hsCodeInstance;
          if (props.isEdit) {
            payload = diffDeep(hsCodeInstance, props.hsCode, true);
            payload.code = props.hsCode!.code!;
          }

          saveAction(payload, {
            onSuccess: () => {
              closeDialog();
            }
          });
        }
      }
    }

    return {
      localDialog,
      hsCodeData,
      valid,
      formRef,
      subtitle,
      closeDialog,
      save,
      clearErrors,
      loading,
      error,
      requiredValidator
    };
  }
});
